'use client';

import React, { useState } from 'react';
import { X, Sparkles, ChevronRight, BookOpen, Coins, Globe, Zap, RefreshCw, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface StoryCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRepeatStory?: () => void;
  originalBookId: string; // Required for API calls
  storyTitle: string;
  currentLanguage?: string;
  showBilingualOption?: boolean;
  onWatchNext?: () => void; // Callback to show WatchNextModal if user closes without action
  onStartGeneration?: (bookId: string, title: string) => void; // Callback when generation starts
}

const AVAILABLE_LANGUAGES = [
  { code: 'es', name: 'Spanish', flag: '🇪🇸', emoji: '🌟' },
  { code: 'fr', name: 'French', flag: '🇫🇷', emoji: '🎨' },
  { code: 'de', name: 'German', flag: '🇩🇪', emoji: '🏰' },
  { code: 'it', name: 'Italian', flag: '🇮🇹', emoji: '🍕' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹', emoji: '⚽' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', emoji: '🐉' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵', emoji: '🌸' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷', emoji: '🎌' },
];

export default function StoryCompletionModal({
  isOpen,
  onClose,
  onRepeatStory,
  originalBookId,
  storyTitle,
  currentLanguage = 'en',
  showBilingualOption = true,
  onWatchNext,
  onStartGeneration,
}: StoryCompletionModalProps) {
  const router = useRouter();
  const [selectedOption, setSelectedOption] = useState<'continue' | 'translate' | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
  const [isHovering, setIsHovering] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const availableLanguages = AVAILABLE_LANGUAGES.filter(
    lang => lang.code !== currentLanguage
  );

  const handleContinueStory = async () => {
    setIsGenerating(true);
    setGenerationStatus('Creating Part 2 of your story...');
    setError('');

    try {
      const response = await fetch('/api/audio-book/continue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originalBookId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to continue story');
      }

      setGenerationStatus(`✨ ${data.title} is being created!`);

      // Close modal and trigger generation progress
      onClose();
      if (onStartGeneration) {
        onStartGeneration(data.bookId, data.title);
      }
    } catch (err) {
      console.error('[Continue Story] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to continue story');
      setIsGenerating(false);
    }
  };

  const handleTranslateStory = async () => {
    setIsGenerating(true);
    setGenerationStatus(`Creating bilingual version in ${AVAILABLE_LANGUAGES.find(l => l.code === selectedLanguage)?.name}...`);
    setError('');

    try {
      const response = await fetch('/api/audio-book/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalBookId,
          targetLanguage: selectedLanguage,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create translation');
      }

      setGenerationStatus('✨ Bilingual book is being created!');

      // Close modal and trigger generation progress
      onClose();
      if (onStartGeneration) {
        const bookId = data.translationId || data.bookId;
        const bookTitle = data.title || storyTitle;
        onStartGeneration(bookId, bookTitle);
      }
    } catch (err) {
      console.error('[Translate Story] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create translation');
      setIsGenerating(false);
    }
  };

  const handleConfirm = () => {
    if (selectedOption === 'continue') {
      handleContinueStory();
    } else if (selectedOption === 'translate') {
      handleTranslateStory();
    }
  };

  const handleClose = () => {
    onClose();
    // If user closes without selecting an option, trigger WatchNext
    if (!selectedOption && !isGenerating && onWatchNext) {
      setTimeout(() => onWatchNext(), 300);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(12px)',
        padding: '1rem',
        animation: 'fadeIn 0.3s ease-out',
      }}
      onClick={isGenerating ? undefined : handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '700px',
          width: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '32px',
          padding: '3rem 2.5rem',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
          animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Close Button */}
        {!isGenerating && (
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '1.5rem',
              right: '1.5rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              color: 'white',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X size={20} />
          </button>
        )}

        {/* Loading State */}
        {isGenerating && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Loader2
              size={60}
              style={{
                animation: 'spin 1s linear infinite',
                margin: '0 auto 2rem',
              }}
            />
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem' }}>
              {generationStatus}
            </h2>
            <p style={{ fontSize: '1rem', opacity: 0.9 }}>
              This will take about 1-2 minutes...
            </p>
            {error && (
              <div
                style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: '12px',
                  color: '#fca5a5',
                }}
              >
                {error}
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        {!isGenerating && (
          <>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '2.5rem', color: 'white' }}>
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  animation: 'pulseGlow 2s ease-in-out infinite',
                }}
              >
                <Sparkles size={40} />
              </div>
              <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.75rem' }}>
                Story Complete!
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1rem', opacity: 0.9 }}>
                <BookOpen size={18} />
                <span style={{ fontWeight: 600, fontStyle: 'italic', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {storyTitle}
                </span>
              </div>
            </div>

            {/* Options Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: onRepeatStory 
                ? (showBilingualOption ? '1fr 1fr 1fr' : '1fr 1fr')
                : (showBilingualOption ? '1fr 1fr' : '1fr'),
              gap: '1rem',
              marginBottom: '2rem',
            }}>
              {/* Repeat Story Option */}
              {onRepeatStory && (
                <button
                  onClick={() => {
                    onRepeatStory();
                    onClose();
                  }}
                  onMouseEnter={() => setIsHovering('repeat')}
                  onMouseLeave={() => setIsHovering(null)}
                  style={{
                    padding: '1.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textAlign: 'left',
                    boxShadow: isHovering === 'repeat'
                      ? '0 12px 32px rgba(16, 185, 129, 0.4)'
                      : '0 8px 24px rgba(16, 185, 129, 0.25)',
                    transform: isHovering === 'repeat'
                      ? 'translateY(-4px) scale(1.02)'
                      : 'translateY(0) scale(1)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem',
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '14px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <RefreshCw 
                        size={24} 
                        color="white"
                        strokeWidth={2.5}
                      />
                    </div>
                    <div>
                      <h3 style={{
                        margin: 0,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: 'white',
                      }}>
                        Replay Story
                      </h3>
                    </div>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: '0.9375rem',
                    lineHeight: '1.5',
                    color: 'rgba(255, 255, 255, 0.9)',
                  }}>
                    Listen to this beautiful story again from the beginning
                  </p>
                </button>
              )}
              
              {/* Continue Story Option */}
              <button
                onClick={() => setSelectedOption('continue')}
                onMouseEnter={() => setIsHovering('continue')}
                onMouseLeave={() => setIsHovering(null)}
                style={{
                  padding: '1.75rem 1.5rem',
                  background: selectedOption === 'continue' 
                    ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                    : 'white',
                  border: selectedOption === 'continue'
                    ? 'none'
                    : '2px solid rgba(102, 126, 234, 0.2)',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textAlign: 'left',
                  boxShadow: selectedOption === 'continue'
                    ? '0 12px 32px rgba(102, 126, 234, 0.4)'
                    : isHovering === 'continue'
                    ? '0 8px 24px rgba(102, 126, 234, 0.15)'
                    : '0 4px 12px rgba(0, 0, 0, 0.05)',
                  transform: selectedOption === 'continue' || isHovering === 'continue'
                    ? 'translateY(-4px) scale(1.02)'
                    : 'translateY(0) scale(1)',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.75rem',
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '14px',
                    background: selectedOption === 'continue'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <ChevronRight 
                      size={24} 
                      color={selectedOption === 'continue' ? 'white' : '#667eea'}
                      strokeWidth={2.5}
                    />
                  </div>
                  <div>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: selectedOption === 'continue' ? 'white' : '#1e293b',
                    }}>
                      Continue Story
                    </h3>
                  </div>
                </div>
                <p style={{
                  margin: '0 0 1rem 0',
                  fontSize: '0.9375rem',
                  lineHeight: '1.5',
                  color: selectedOption === 'continue' ? 'rgba(255, 255, 255, 0.9)' : '#64748b',
                }}>
                  Generate Part 2 that continues the adventure
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selectedOption === 'continue' ? 'rgba(255, 255, 255, 0.8)' : '#10b981',
                }}>
                  <Coins size={16} />
                  <span>10 credits</span>
                </div>
              </button>

              {/* Learn in New Language Option */}
              {showBilingualOption && (
                <button
                  onClick={() => setSelectedOption('translate')}
                  onMouseEnter={() => setIsHovering('translate')}
                  onMouseLeave={() => setIsHovering(null)}
                  style={{
                    padding: '1.75rem 1.5rem',
                    background: selectedOption === 'translate'
                      ? 'linear-gradient(135deg, #10b981, #06b6d4)'
                      : 'white',
                    border: selectedOption === 'translate'
                      ? 'none'
                      : '2px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textAlign: 'left',
                    boxShadow: selectedOption === 'translate'
                      ? '0 12px 32px rgba(16, 185, 129, 0.4)'
                      : isHovering === 'translate'
                      ? '0 8px 24px rgba(16, 185, 129, 0.15)'
                      : '0 4px 12px rgba(0, 0, 0, 0.05)',
                    transform: selectedOption === 'translate' || isHovering === 'translate'
                      ? 'translateY(-4px) scale(1.02)'
                      : 'translateY(0) scale(1)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.75rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '14px',
                        background: selectedOption === 'translate'
                          ? 'rgba(255, 255, 255, 0.2)'
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(6, 182, 212, 0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Globe 
                          size={24} 
                          color={selectedOption === 'translate' ? 'white' : '#10b981'}
                          strokeWidth={2.5}
                        />
                      </div>
                      <div>
                        <h3 style={{
                          margin: 0,
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: selectedOption === 'translate' ? 'white' : '#1e293b',
                        }}>
                          Learn in New Language
                        </h3>
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: selectedOption === 'translate' ? 'rgba(255, 255, 255, 0.8)' : '#10b981',
                        }}>
                          BILINGUAL MODE
                        </span>
                      </div>
                    </div>
                  </div>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontSize: '0.9375rem',
                    lineHeight: '1.5',
                    color: selectedOption === 'translate' ? 'rgba(255, 255, 255, 0.9)' : '#64748b',
                  }}>
                    Learn this story in a new language
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: selectedOption === 'translate' ? 'rgba(255, 255, 255, 0.8)' : '#10b981',
                  }}>
                    <Coins size={16} />
                    <span>10 credits</span>
                  </div>
                </button>
              )}
            </div>

            {/* Language Selection */}
            {selectedOption === 'translate' && (
              <div style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                animation: 'slideUp 0.3s ease-out',
              }}>
                <p style={{
                  margin: '0 0 1rem 0',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Choose Language
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '0.75rem',
                }}>
                  {availableLanguages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => setSelectedLanguage(lang.code)}
                      style={{
                        padding: '0.875rem',
                        background: selectedLanguage === lang.code
                          ? 'linear-gradient(135deg, #10b981, #06b6d4)'
                          : 'white',
                        border: selectedLanguage === lang.code
                          ? 'none'
                          : '2px solid rgba(16, 185, 129, 0.2)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: selectedLanguage === lang.code ? 'white' : '#1e293b',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <span style={{ fontSize: '1.5rem' }}>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Button */}
            {selectedOption && (
              <button
                onClick={handleConfirm}
                style={{
                  width: '100%',
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  boxShadow: '0 8px 24px rgba(251, 191, 36, 0.4)',
                  animation: 'slideUp 0.3s ease-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(251, 191, 36, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(251, 191, 36, 0.4)';
                }}
              >
                <Zap size={20} fill="currentColor" />
                <span>
                  {selectedOption === 'continue' ? 'Create Part 2' : 'Create Bilingual Version'}
                </span>
              </button>
            )}
          </>
        )}

        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulseGlow {
            0%, 100% { opacity: 0.8; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.05); }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
